import QuickLocationUpdateButton from './../workflow/QuickLocationUpdateButton';
import { AddressLink } from '../common/AddressLink';
import SMSPreviewModal from './../communications/SMSPreviewModal';
import WorkflowQuickUpdate from './../workflow/WorkflowQuickUpdate';
import EmailPreviewModal from './../communications/EmailPreviewModal';
import { getSignatureBlock, updateAfterpayDraft } from '../communications/emailSignature';
import axios from 'axios';
import React, { useState, useEffect, useRef } from 'react';
import { apiClient, showToast } from '../../utils/apiClient';
import { API } from '../../config';
import PriorPetsDisplay from './PriorPetsDisplay';
import { useAuth } from '../../context/AuthContext';
import { useRouter } from '../common/Router';
import { formatDate, formatDateTime, formatNotesWithBullets, getDayOfWeek, formatDateWithDay, getLocationDisplay, getLocationChipColor, getRowAlert, formatDatetimeForInput, parseUTCDatetime, getTodayInAEST, isPrepaymentApproaching, hasAppointments, getAppointmentSummary, getPaymentSummary } from '../../utils/helpers';
import { getAvailableUrns, getAvailableKeepsakes, calculateBaseServicePrice, getProductPrice, getCustomProductIcon, getProductIcon, formatKeepsakesForDisplay, parseQuantityFromString, convertToCartFormat, calculateTotalCost, initializePricing, getViewingRoomPrice } from '../../utils/pricingHelpers';
import AIChatPanel from '../ai/AIChatPanel';
import QBInvoicePanel from '../quickbooks/QBInvoicePanel';

const groupSelections = (items = []) => {
  const grouped = [];
  items.forEach((name) => {
    const existing = grouped.find((entry) => entry.name === name);
    if (existing) {
      existing.count += 1;
    } else {
      grouped.push({ name, count: 1 });
    }
  });
  return grouped;
};

const PetView = ({ petId }) => {
  const { user } = useAuth();
  const { navigate } = useRouter();
  const [pet, setPet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLocationHistory, setShowLocationHistory] = useState(false);
  
  // SMS Modal States
  const [showSMSModal, setShowSMSModal] = useState(false);
  const [smsModalData, setSMSModalData] = useState({
    petId: null,
    stepKey: null,
    templateKey: null,
    primaryTemplate: null,
    alternativeTemplates: [],
    renderedBody: '',
    clientName: '',
    petName: '',
    toPhone: '',
    loading: false,
    sending: false,
    error: null,
    success: false
  });
  
  // Email Modal States
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailModalData, setEmailModalData] = useState({
    petId: null,
    stepKey: null,
    templateKey: null,
    subject: '',
    renderedBody: '',
    clientName: '',
    petName: '',
    toEmail: '',
    loading: false,
    sending: false,
    error: null,
    success: false,
    isCertificateEmail: false
  });

  const fetchPet = async () => {
    try {
      const response = await apiClient.get(`/pets/${petId}`);
      setPet(response.data);
    } catch (error) {
      console.error('Failed to fetch pet:', error);
      if (error.response?.status === 404) {
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchPet();
    initializePricing(API.replace('/api', ''));
  }, [petId]);
  
  // SMS Workflow Functions
  const openSMSModal = async (petId, stepKey) => {
    try {
      setSMSModalData(prev => ({ ...prev, petId, stepKey, loading: true, error: null, success: false, renderedBody: '' }));
      setShowSMSModal(true);
      
      // Get templates for this step
      const templatesResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sms/templates/${stepKey}`);
      if (!templatesResponse.ok) {
        throw new Error('Failed to load SMS templates');
      }
      const templatesData = await templatesResponse.json();
      
      // Preview SMS with primary template
      const previewResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sms/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          record_id: petId,
          template_key: templatesData.primary_template.key
        })
      });
      
      if (!previewResponse.ok) {
        throw new Error('Failed to preview SMS');
      }
      const previewData = await previewResponse.json();
      
      setSMSModalData({
        petId,
        stepKey,
        templateKey: templatesData.primary_template.key,
        primaryTemplate: templatesData.primary_template,
        alternativeTemplates: templatesData.alternative_templates || [],
        renderedBody: previewData.body_rendered,
        clientName: previewData.client_name,
        petName: previewData.pet_name,
        toPhone: previewData.to_phone,
        loading: false,
        sending: false,
        error: null,
        success: false
      });
      
    } catch (error) {
      console.error('Error opening SMS modal:', error);
      setSMSModalData(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
    }
  };
  
  const changeTemplate = async (newTemplateKey) => {
    try {
      setSMSModalData(prev => ({ ...prev, loading: true, error: null }));
      
      const previewResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sms/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          record_id: smsModalData.petId,
          template_key: newTemplateKey
        })
      });
      
      if (!previewResponse.ok) {
        throw new Error('Failed to preview SMS with new template');
      }
      const previewData = await previewResponse.json();
      
      setSMSModalData(prev => ({
        ...prev,
        templateKey: newTemplateKey,
        renderedBody: previewData.body_rendered,
        loading: false
      }));
      
    } catch (error) {
      console.error('Error changing template:', error);
      setSMSModalData(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
    }
  };
  
  const sendSMS = async (scheduledTime = null) => {
    try {
      setSMSModalData(prev => ({ ...prev, sending: true, error: null }));
      
      // Prepare scheduled time for backend
      // datetime-local returns: "2025-11-17T15:21" (no timezone)
      // Backend will interpret this as AEST and convert to UTC
      const payload = {
        record_id: smsModalData.petId,
        template_key: smsModalData.templateKey,
        step_key: smsModalData.stepKey,
        body_rendered: smsModalData.renderedBody,
        sent_by_user_id: user?.id
      };
      
      if (scheduledTime) {
        // Send the datetime string as-is - backend will handle AEST→UTC conversion
        payload.scheduled_send_time = scheduledTime;
        console.log('🕐 Scheduling SMS for (AEST):', scheduledTime);
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sms/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send SMS');
      }
      
      const result = await response.json();
      
      // Show appropriate success message
      if (result.scheduled) {
        showToast(`📅 SMS scheduled for ${new Date(scheduledTime).toLocaleString('en-AU', { 
          weekday: 'short', 
          month: 'short', 
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        })}`, 'success');
      } else {
        showToast('✅ SMS sent successfully!', 'success');
      }
      
      setSMSModalData(prev => ({
        ...prev,
        sending: false,
        success: true,
        scheduled: !!result.scheduled
      }));
      
      // Refresh pet to show updated workflow status (only if sent immediately)
      if (!result.scheduled) {
        await fetchPet();
      }
      
      // Auto-close modal after 2 seconds
      setTimeout(() => {
        setShowSMSModal(false);
        setSMSModalData({
          petId: null,
          stepKey: null,
          templateKey: null,
          primaryTemplate: null,
          alternativeTemplates: [],
          renderedBody: '',
          clientName: '',
          petName: '',
          toPhone: '',
          loading: false,
          sending: false,
          error: null,
          success: false
        });
      }, 2000);
      
    } catch (error) {
      console.error('Error sending SMS:', error);
      setSMSModalData(prev => ({
        ...prev,
        sending: false,
        error: error.message
      }));
    }
  };


  // Email Workflow Functions
  const openEmailModal = async (petId, stepKey) => {
    try {
      setEmailModalData(prev => ({
        ...prev,
        loading: true,
        error: null,
        availableTemplates: [],
        previousPetInfo: null,
        templateKey: null,
        subject: '',
        renderedBody: '',
        category: null,
      }));
      setShowEmailModal(true);
      
      // Determine the email template category based on step key
      const categoryMap = {
        'tree_planting_email': 'tree_planting',
        'tree_planting_reply_email': 'tree_planting_reply',
        'other_emails': 'other_emails',
        'review_request': 'review_request'
      };
      const category = categoryMap[stepKey];
      
      if (category) {
        // Fetch available templates for this category
        const templatesResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/email/templates/${category}?record_id=${petId}`);
        if (!templatesResponse.ok) throw new Error('Failed to fetch email templates');
        const templatesData = await templatesResponse.json();
        
        // Default to first template
        const defaultTemplate = templatesData.templates[0];
        const templateKey = defaultTemplate.key;

        // Pre-load afterpay_link from pet record (for AfterPay template resend)
        let petAfterpayLink = null;
        try {
          const petRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pets/${petId}`);
          if (petRes.ok) {
            const petData = await petRes.json();
            petAfterpayLink = petData.afterpay_link || null;
          }
        } catch {}
        
        // Preview the default template
        const previewResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/email/preview`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ record_id: petId, template_key: templateKey, afterpay_link: petAfterpayLink })
        });
        if (!previewResponse.ok) throw new Error('Failed to preview email');
        const previewData = await previewResponse.json();
        
        setEmailModalData({
          petId,
          stepKey,
          templateKey,
          subject: previewData.subject,
          renderedBody: previewData.body_rendered,
          clientName: previewData.client_name,
          petName: previewData.pet_name,
          toEmail: previewData.to_email,
          signature: previewData.signature,
          signatureHtml: getSignatureBlock(previewData.body_rendered),
          loading: false,
          sending: false,
          error: null,
          success: false,
          availableTemplates: templatesData.templates,
          previousPetInfo: templatesData.previous_pet_info,
          afterpayLink: petAfterpayLink || '',
          category: category
        });
      } else {
        // Fallback for other email steps (certificate etc.)
        const templateKey = 'email.tree_planting';
        const previewResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/email/preview`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ record_id: petId, template_key: templateKey })
        });
        if (!previewResponse.ok) throw new Error('Failed to preview email');
        const previewData = await previewResponse.json();
        
        setEmailModalData({
          petId,
          stepKey,
          templateKey,
          subject: previewData.subject,
          renderedBody: previewData.body_rendered,
          clientName: previewData.client_name,
          petName: previewData.pet_name,
          toEmail: previewData.to_email,
          signature: previewData.signature,
          signatureHtml: getSignatureBlock(previewData.body_rendered),
          loading: false,
          sending: false,
          error: null,
          success: false,
          availableTemplates: [],
          previousPetInfo: null
        });
      }
    } catch (error) {
      console.error('Error opening email modal:', error);
      setEmailModalData(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
    }
  };

  const changeEmailTemplate = async (templateKey, afterpayLinkOverride) => {
    try {
      setEmailModalData(prev => ({ ...prev, loading: true }));
      const previewResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/email/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          record_id: emailModalData.petId,
          template_key: templateKey,
          afterpay_link: (afterpayLinkOverride !== undefined ? afterpayLinkOverride : emailModalData.afterpayLink) || null,
          signature: emailModalData.signature
        })
      });
      if (!previewResponse.ok) throw new Error('Failed to preview email');
      const previewData = await previewResponse.json();
      
      setEmailModalData(prev => ({
        ...prev,
        templateKey,
        subject: previewData.subject,
        renderedBody: previewData.body_rendered,
        signature: previewData.signature,
        signatureHtml: getSignatureBlock(previewData.body_rendered),
        loading: false
      }));
    } catch (error) {
      console.error('Error changing email template:', error);
      setEmailModalData(prev => ({ ...prev, loading: false, error: error.message }));
    }
  };

  const sendEmail = async () => {
    try {
      setEmailModalData(prev => ({ ...prev, sending: true, error: null }));
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/email/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          record_id: emailModalData.petId,
          template_key: emailModalData.templateKey,
          step_key: emailModalData.stepKey,
          subject: emailModalData.subject,
          body_rendered: emailModalData.renderedBody,
          signature: emailModalData.signature,
          sent_by_user_id: user?.id,
          afterpay_link: emailModalData.afterpayLink || null
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send email');
      }
      
      setEmailModalData(prev => ({
        ...prev,
        sending: false,
        success: true
      }));
      
      // Refresh pet to show updated workflow status
      await fetchPet();
      
      // Auto-close modal after 2 seconds
      setTimeout(() => {
        setShowEmailModal(false);
        setEmailModalData({
          petId: null,
          stepKey: null,
          templateKey: null,
          subject: '',
          renderedBody: '',
          clientName: '',
          petName: '',
          toEmail: '',
          loading: false,
          sending: false,
          error: null,
          success: false
        });
      }, 2000);
      
    } catch (error) {
      console.error('Error sending email:', error);
      setEmailModalData(prev => ({
        ...prev,
        sending: false,
        error: error.message
      }));
    }
  };




  // Certificate Modal Functions
  const openCertificateModal = async (petId) => {
    try {
      setEmailModalData(prev => ({ ...prev, loading: true, error: null }));
      setShowEmailModal(true);
      
      // Preview certificate email
      const previewResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/certificates/preview/${petId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!previewResponse.ok) {
        throw new Error('Failed to preview certificate email');
      }
      const previewData = await previewResponse.json();
      
      setEmailModalData({
        petId,
        stepKey: 'certificate_email',
        templateKey: 'certificate_email',
        subject: previewData.subject,
        renderedBody: previewData.body_html,
        signature: previewData.signature,
        signatureHtml: getSignatureBlock(previewData.body_html),
        clientName: previewData.client_name,
        petName: previewData.pet_name,
        toEmail: previewData.to_email,
        loading: false,
        sending: false,
        error: null,
        success: false,
        isCertificateEmail: true,  // Flag to handle different send logic
        envCertType: previewData.environmental_certificate_type
      });
      
    } catch (error) {
      console.error('Error opening certificate modal:', error);
      setEmailModalData(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));
    }
  };

  const sendCertificates = async () => {
    try {
      setEmailModalData(prev => ({ ...prev, sending: true, error: null }));
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/certificates/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pet_id: emailModalData.petId,
          custom_subject: emailModalData.subject,
          custom_body_html: emailModalData.renderedBody,
          signature: emailModalData.signature
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send certificates');
      }
      
      const data = await response.json();
      
      // Update modal to show success
      setEmailModalData(prev => ({
        ...prev,
        sending: false,
        success: true
      }));
      
      // Show toast with certificate reference
      showToast(`✅ Certificates sent successfully! Reference: ${data.certificate_reference}`, 'success');
      
      // Fetch updated pet data to refresh UI
      await fetchPet();  // Use fetchPet instead of fetchPets (PetView function)
      
      // Close modal after 2 seconds
      setTimeout(() => {
        setShowEmailModal(false);
        setEmailModalData({
          petId: null,
          stepKey: null,
          templateKey: null,
          subject: '',
          renderedBody: '',
          clientName: '',
          petName: '',
          toEmail: '',
          loading: false,
          sending: false,
          error: null,
          success: false,
          isCertificateEmail: false
        });
      }, 2000);
      
    } catch (error) {
      console.error('Error sending certificates:', error);
      setEmailModalData(prev => ({
        ...prev,
        sending: false,
        error: error.message
      }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading pet record...</p>
        </div>
      </div>
    );
  }

  if (!pet) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Pet record not found</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending in our care SMS': return 'bg-red-100 text-red-800 border-red-200';
      case 'Awaiting Cremation SMS': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Awaiting Tree Planting Email': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Awaiting Pickup Ashes SMS': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Awaiting ashes collection booking': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'Awaiting Certificate Email': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Awaiting Review SMS': return 'bg-white text-purple-800 border-purple-300';
      case 'Completed': return 'bg-green-100 text-green-800 border-green-200'; // Only green when truly completed (review SMS sent)
      // Legacy statuses
      case 'In Progress': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Awaiting Keepsake': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'Ready for Pickup': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="mx-auto px-6">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/')}
                className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
              >
                <svg className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-2xl font-bold text-gray-900">{pet.pet_name}</h1>
              <span className={`ml-4 inline-flex px-3 py-1 text-sm font-semibold rounded-full border ${getStatusColor(pet.status)}`}>
                {pet.status === 'Awaiting Review SMS' ? 'Awaiting Review Request' : pet.status}
              </span>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  console.log('🔧 PetView Edit button clicked for pet:', pet.id);
                  window.location.href = `/pets/${pet.id}/edit`;
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium"
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto px-6 py-8">
        {/* Complimentary Urn Banner — prominent reminder when urn not yet selected */}
        {pet.vet_staff_benefit === 'complimentary_urn' && (!pet.urns_selected || pet.urns_selected.length === 0) && (
          <div className="mb-6 bg-purple-600 text-white rounded-xl px-5 py-4 shadow-lg flex items-center gap-4" data-testid="comp-urn-banner">
            <div className="flex-shrink-0 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-xl">
              <i className="fa-solid fa-gift"></i>
            </div>
            <div className="flex-1">
              <p className="font-bold text-base">Complimentary Urn — No urn selected yet</p>
              <p className="text-purple-100 text-sm mt-0.5">This client is entitled to a free urn. Please edit the record and select an urn before invoicing.</p>
            </div>
            <button
              onClick={() => window.location.href = `/pets/${pet.id}/edit`}
              className="flex-shrink-0 bg-white text-purple-700 font-semibold px-4 py-2 rounded-lg text-sm hover:bg-purple-50 transition"
              data-testid="comp-urn-edit-btn"
            >
              Select Urn
            </button>
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Column 1: Basic Information */}
          <div className="space-y-6">
            {/* Date Arrived */}
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Date pet arrived at our care</h3>
              <div className="flex items-center space-x-4">
                <div>
                  <p className="text-3xl font-bold text-blue-600">{getDayOfWeek(pet.arrival_date)}</p>
                  <p className="text-sm text-gray-500">Day of Week</p>
                </div>
                <div>
                  <p className="text-xl font-semibold text-gray-900">{formatDate(pet.arrival_date)}</p>
                  <p className="text-sm text-gray-500">Arrival Date</p>
                </div>
              </div>
              
              {/* Prepayment Information */}
              {pet.prepayment && (
                <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                    <span className="text-sm font-medium text-green-800">Prepayment</span>
                    {pet.prepayment_date && (
                      <span className="text-sm text-green-600 ml-2">
                        • {formatDate(pet.prepayment_date)}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-green-600 mt-1 ml-6">
                    Workflow starts with first SMS
                  </p>
                </div>
              )}
            </div>

            {/* Client Information */}
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Client Information</h3>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Full Name</dt>
                  <dd className="text-sm text-gray-900">{pet.client_name}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Phone Number</dt>
                  <dd className="text-sm text-gray-900">{pet.phone_number}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Email</dt>
                  <dd className="text-sm text-gray-900">{pet.email}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Address</dt>
                  <dd className="text-sm text-gray-900"><AddressLink address={pet.address} postcode={pet.postcode} testId="pet-customer-address-link" /></dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Postcode</dt>
                  <dd className="text-sm text-gray-900">{pet.postcode}</dd>
                </div>
                {pet.vet_clinic && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Vet Clinic</dt>
                    <dd className="text-sm text-gray-900">{pet.vet_clinic}</dd>
                  </div>
                )}

                {/* Vet Staff Benefit Badge */}
                {pet.vet_staff_benefit && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Vet Staff Benefit</dt>
                    <dd className="mt-0.5">
                      {pet.vet_staff_benefit === 'discount_10' ? (
                        <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 text-xs font-semibold px-2.5 py-1 rounded-full border border-green-200">
                          🏷️ 10% Vet Staff Discount
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-800 text-xs font-semibold px-2.5 py-1 rounded-full border border-purple-200">
                          🏺 Complimentary Urn
                        </span>
                      )}
                    </dd>
                  </div>
                )}
                
                {/* Prior Pets for Returning Clients */}
                <PriorPetsDisplay phoneNumber={pet.phone_number} email={pet.email} currentPetId={pet.id} />
              </dl>
            </div>

            {/* Pet Information */}
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Pet Information</h3>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Pet Name</dt>
                  <dd className="text-sm text-gray-900">{pet.pet_name}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Pet Type</dt>
                  <dd className="text-sm text-gray-900">{pet.pet_type || 'Dog'}</dd>
                </div>
                {pet.pet_type === 'Exotic Pet' && pet.exotic_subcategory && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Exotic Pet Type</dt>
                    <dd className="text-sm text-gray-900">{pet.exotic_subcategory}</dd>
                  </div>
                )}
                {pet.pet_type && pet.weight_kg && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Base Service Price</dt>
                    <dd className="text-sm text-gray-900">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        ${calculateBaseServicePrice(pet.pet_type, pet.weight_kg)}
                      </span>
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm font-medium text-gray-500">Gender</dt>
                  <dd className="text-sm text-gray-900">{pet.sex === 'M' ? 'Male' : pet.sex === 'F' ? 'Female' : 'Unknown'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Breed</dt>
                  <dd className="text-sm text-gray-900">{pet.breed}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Weight</dt>
                  <dd className="text-sm text-gray-900">{pet.weight_kg} kg</dd>
                </div>
                {pet.age_years && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Age</dt>
                    <dd className="text-sm text-gray-900">
                      {pet.age_years} years
                    </dd>
                  </div>
                )}
                {pet.microchip_number && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Microchip (#)</dt>
                    <dd className="text-sm text-gray-900 font-mono">{pet.microchip_number}</dd>
                  </div>
                )}
              </dl>
            </div>
          </div>

          {/* Column 2: Location & Service Details */}
          <div className="space-y-6">
            {/* On-site Location (NEW) */}
            <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-indigo-400">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-gray-900">🏷️ On-site Location & Activity</h3>
                <QuickLocationUpdateButton pet={pet} onUpdate={fetchPet} />
              </div>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Current Location</dt>
                  <dd className="text-sm text-gray-900">
                    <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full border ${getLocationChipColor(pet.location_area)}`}>
                      {getLocationDisplay(pet.location_area, pet.location_detail)}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                  <dd className="text-sm text-gray-900">{formatDateTime(pet.location_updated_at)}</dd>
                </div>
              </dl>
              
              {/* Activity History (Location + Appointments) */}
              {pet.location_history && pet.location_history.length > 0 && (
                <div className="mt-4">
                  <button
                    onClick={() => setShowLocationHistory(!showLocationHistory)}
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  >
                    {showLocationHistory ? '🔽' : '▶️'} Activity History ({pet.location_history.length})
                  </button>
                  
                  {showLocationHistory && (
                    <div className="mt-3 border rounded-lg">
                      <div className="max-h-64 overflow-y-auto">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left font-medium text-gray-500">Activity</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-500">Date & Time</th>
                              <th className="px-3 py-2 text-left font-medium text-gray-500">By</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {pet.location_history.slice(-10).reverse().map((entry, index) => (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-3 py-2">
                                  {entry.type === 'appointment' ? (
                                    <span className="text-blue-700">
                                      📅 {entry.detail || entry.area}
                                    </span>
                                  ) : (
                                    getLocationDisplay(entry.area, entry.detail)
                                  )}
                                </td>
                                <td className="px-3 py-2 text-gray-600">
                                  {formatDateTime(entry.changed_at)}
                                </td>
                                <td className="px-3 py-2 text-gray-600">
                                  {entry.changed_by || 'Unknown'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Service & Workflow */}
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Service & Workflow</h3>
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Vet Collection</dt>
                  <dd className="text-sm text-gray-900">{pet.arrival_method || 'Not specified'}</dd>
                </div>
                
                {/* Always show Urns section */}
                <div>
                  <dt className="text-sm font-medium text-gray-500">Urns Selected</dt>
                  <dd className="text-sm text-gray-900">
                    {pet.urns_selected && pet.urns_selected.length > 0 ? (
                      <div className="flex flex-wrap gap-2 mt-1">
                        {groupSelections(pet.urns_selected).map(({ name: urn, count }, index) => {
                          const customIcon = getCustomProductIcon(urn, pet.pet_type);
                          return (
                            <span
                              key={index}
                              data-testid={`pet-urn-chip-${index}`}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full"
                            >
                              {customIcon && customIcon.type === 'image' ? (
                                <img 
                                  src={customIcon.src} 
                                  alt={customIcon.alt}
                                  className="w-4 h-4 mr-1 rounded object-contain"
                                />
                              ) : (
                                <span className="mr-1">{getProductIcon(urn)}</span>
                              )}
                              {urn}
                              {count > 1 && <span className="ml-1 font-bold">× {count}</span>}
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="text-gray-400 italic">No urns selected yet</span>
                    )}
                  </dd>
                </div>
                
                {/* Always show Keepsakes section */}
                <div>
                  <dt className="text-sm font-medium text-gray-500">Keepsakes Selected</dt>
                  <dd className="text-sm text-gray-900">
                    {pet.keepsakes_selected && pet.keepsakes_selected.length > 0 ? (
                      <div className="flex flex-wrap gap-2 mt-1">
                        {groupSelections(pet.keepsakes_selected).map(({ name: keepsake, count }, index) => {
                          const customIcon = getCustomProductIcon(keepsake, pet.pet_type);
                          return (
                            <span
                              key={index}
                              data-testid={`pet-keepsake-chip-${index}`}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full"
                            >
                              {customIcon && customIcon.type === 'image' ? (
                                <img 
                                  src={customIcon.src} 
                                  alt={customIcon.alt}
                                  className="w-4 h-4 mr-1 rounded object-contain"
                                />
                              ) : (
                                <span className="mr-1">{getProductIcon(keepsake)}</span>
                              )}
                              {keepsake}
                              {count > 1 && <span className="ml-1 font-bold">× {count}</span>}
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="text-gray-400 italic">No keepsakes selected yet</span>
                    )}
                  </dd>
                </div>
                
                {pet.keepsakes_notes && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Keepsakes Notes</dt>
                    <dd className="text-sm text-gray-900">{pet.keepsakes_notes}</dd>
                  </div>
                )}
                
                {/* Always show Jewelry section */}
                <div>
                  <dt className="text-sm font-medium text-gray-500">Jewelry Selected</dt>
                  <dd className="text-sm text-gray-900">
                    {pet.jewelry_selected && pet.jewelry_selected.length > 0 ? (
                      <div className="flex flex-wrap gap-2 mt-1">
                        {groupSelections(pet.jewelry_selected).map(({ name: jewelry, count }, index) => {
                          const customIcon = getCustomProductIcon(jewelry, pet.pet_type);
                          return (
                            <span
                              key={index}
                              data-testid={`pet-jewelry-chip-${index}`}
                              className="inline-flex items-center px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full"
                            >
                              {customIcon && customIcon.type === 'image' ? (
                                <img 
                                  src={customIcon.src} 
                                  alt={customIcon.alt}
                                  className="w-4 h-4 mr-1 rounded object-contain"
                                />
                              ) : (
                                <span className="mr-1">{getProductIcon(jewelry)}</span>
                              )}
                              {jewelry}
                              {count > 1 && <span className="ml-1 font-bold">× {count}</span>}
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="text-gray-400 italic">No jewelry selected yet</span>
                    )}
                  </dd>
                </div>
                {pet.jewelry_notes && pet.jewelry_notes.trim() && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Notes</dt>
                    <dd className="text-sm text-gray-900 whitespace-pre-line mt-1">{formatNotesWithBullets(pet.jewelry_notes)}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm font-medium text-gray-500">Certificate Type</dt>
                  <dd className="text-sm text-gray-900">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      pet.certificate_type === 'Digital' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {pet.certificate_type || 'Digital'}
                    </span>
                  </dd>
                </div>
                {pet.viewing_room && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Viewing Room</dt>
                    <dd className="text-sm text-gray-900">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {pet.viewing_room} - ${getViewingRoomPrice(pet.viewing_room)}
                      </span>
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm font-medium text-gray-500">Cremation SMS Sent</dt>
                  <dd className="text-sm text-gray-900 mt-1">
                    {pet.aquacremation_sms_date ? (
                      <>
                        {parseUTCDatetime(pet.aquacremation_sms_date).toLocaleDateString('en-AU', { 
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })} at {parseUTCDatetime(pet.aquacremation_sms_date).toLocaleTimeString('en-AU', { 
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true
                        })}
                      </>
                    ) : (
                      <span className="text-gray-400">Not sent</span>
                    )}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">Tree Planting Email Sent</dt>
                  <dd className="text-sm text-gray-900">{pet.tree_planting_email_sent ? 'Yes' : 'No'}</dd>
                </div>
                {pet.transportation_fees && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Transportation Fees</dt>
                    <dd className="text-sm text-gray-900">
                      {Array.isArray(pet.transportation_fees) ? (
                        <div className="space-y-1">
                          {pet.transportation_fees.filter(option => {
                            // Only show free transportation options here (paid ones shown in price breakdown)
                            const price = getProductPrice(option, 'transportation');
                            return price === 0;
                          }).map((option, index) => (
                            <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-1 mb-1">
                              {option}
                            </span>
                          ))}
                        </div>
                      ) : (
                        // Only show if it's a free option
                        getProductPrice(pet.transportation_fees, 'transportation') === 0 && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {pet.transportation_fees}
                          </span>
                        )
                      )}
                    </dd>
                  </div>
                )}
                {(pet.aquacremation_sms_date || pet.aquacremation_sms) && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Cremation Date</dt>
                    <dd className="text-sm text-gray-900">
                      {pet.aquacremation_sms_date ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                          🔥 {formatDateTime(pet.aquacremation_sms_date)}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          🔥 SMS Sent (Date not recorded)
                        </span>
                      )}
                    </dd>
                  </div>
                )}
                {pet.ashes_collection_datetime && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Ashes Collection Booked</dt>
                    <dd className="text-sm text-gray-900">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        📅 {formatDateTime(pet.ashes_collection_datetime)}
                      </span>
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* SMS/Email Workflow (NEW) */}
            <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-green-400">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-gray-900">📧 SMS/Email Workflow</h3>
                <WorkflowQuickUpdate pet={pet} onUpdate={fetchPet} openSMSModal={openSMSModal} openEmailModal={openEmailModal} openCertificateModal={openCertificateModal} />
              </div>
              
              <div className="space-y-4">
                {/* Workflow Steps */}
                <div className="grid grid-cols-1 gap-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <div className={`w-4 h-4 rounded-full ${pet.booking_confirmation_sms ? 'bg-green-500' : 'bg-gray-300'} mr-3`}></div>
                      <span className="text-sm text-gray-900">Booking Confirmation SMS</span>
                      
                    </div>
                    {pet.booking_confirmation_sms_date && (
                      <span className="text-xs text-gray-500">{formatDateTime(pet.booking_confirmation_sms_date)}</span>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <div className={`w-4 h-4 rounded-full ${pet.home_pickup_delivery_sms ? 'bg-green-500' : 'bg-gray-300'} mr-3`}></div>
                      <span className="text-sm text-gray-900">Home Pick up/Delivery SMS</span>
                      
                    </div>
                    {pet.home_pickup_delivery_sms_date && (
                      <span className="text-xs text-gray-500">{formatDateTime(pet.home_pickup_delivery_sms_date)}</span>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <div className={`w-4 h-4 rounded-full ${pet.euthanasia_support_sms ? 'bg-green-500' : 'bg-gray-300'} mr-3`}></div>
                      <span className="text-sm text-gray-900">Euthanasia Support SMS</span>
                      
                    </div>
                    {pet.euthanasia_support_sms_date && (
                      <span className="text-xs text-gray-500">{formatDateTime(pet.euthanasia_support_sms_date)}</span>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                    <div className="flex items-center">
                      <div className={`w-4 h-4 rounded-full ${pet.pet_in_care_sms ? 'bg-green-500' : 'bg-red-400'} mr-3`}></div>
                      <span className="text-sm font-medium text-gray-900">Pet in our Care SMS</span>
                      
                    </div>
                    {pet.pet_in_care_sms_date && (
                      <span className="text-xs text-gray-500">{formatDateTime(pet.pet_in_care_sms_date)}</span>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                    <div className="flex items-center">
                      <div className={`w-4 h-4 rounded-full ${pet.aquacremation_sms ? 'bg-green-500' : 'bg-red-400'} mr-3`}></div>
                      <span className="text-sm font-medium text-gray-900">Cremation SMS</span>
                      
                    </div>
                    {pet.aquacremation_sms_date && (
                      <span className="text-xs text-blue-600 font-medium">{formatDateTime(pet.aquacremation_sms_date)}</span>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                    <div className="flex items-center">
                      <div className={`w-4 h-4 rounded-full ${pet.tree_planting_email ? 'bg-green-500' : 'bg-red-400'} mr-3`}></div>
                      <span className="text-sm font-medium text-gray-900">Tree Planting Email</span>
                      
                      {pet.tree_planting_option && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded ml-2">{pet.tree_planting_option}</span>
                      )}
                    </div>
                    {pet.tree_planting_email_date && (
                      <span className="text-xs text-gray-500">{formatDateTime(pet.tree_planting_email_date)}</span>
                    )}
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-testid="tree-planting-reply-status">
                    <div className="flex items-center">
                      <div className={`w-4 h-4 rounded-full ${pet.tree_planting_reply_email ? 'bg-green-500' : 'bg-gray-300'} mr-3`}></div>
                      <span className="text-sm font-medium text-gray-900">Tree Planting Email Replies</span>
                    </div>
                    {pet.tree_planting_reply_email_date && (
                      <span className="text-xs text-gray-500">{formatDateTime(pet.tree_planting_reply_email_date)}</span>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <div className={`w-4 h-4 rounded-full ${pet.pickup_ashes_sms ? 'bg-green-500' : 'bg-gray-300'} mr-3`}></div>
                      <span className="text-sm text-gray-900">Pick up Ashes SMS</span>
                      
                    </div>
                    {pet.pickup_ashes_sms_date && (
                      <span className="text-xs text-gray-500">{formatDateTime(pet.pickup_ashes_sms_date)}</span>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <div className={`w-4 h-4 rounded-full ${pet.ashes_collection_booked ? 'bg-green-500' : 'bg-gray-300'} mr-3`}></div>
                      <span className="text-sm text-gray-900">Ashes collection Booked</span>
                      
                    </div>
                    {pet.ashes_collection_datetime && (
                      <span className="text-xs text-gray-500">{formatDateTime(pet.ashes_collection_datetime)}</span>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                    <div className="flex items-center">
                      <div className={`w-4 h-4 rounded-full ${pet.certificate_email ? 'bg-green-500' : 'bg-red-400'} mr-3`}></div>
                      <span className="text-sm font-medium text-gray-900">Certificate Email</span>
                      
                    </div>
                    {pet.certificate_email_date && (
                      <span className="text-xs text-gray-500">{formatDateTime(pet.certificate_email_date)}</span>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                    <div className="flex items-center">
                      <div className={`w-4 h-4 rounded-full ${pet.review_sms ? 'bg-green-500' : 'bg-red-400'} mr-3`}></div>
                      <span className="text-sm font-medium text-gray-900" data-testid="pet-review-request-label">Review Request</span>
                      
                    </div>
                    <div className="text-xs text-gray-500 text-right" data-testid="pet-review-request-summary">
                      {pet.review_sms_date && <div>{formatDateTime(pet.review_sms_date)}</div>}
                      {['sms', 'email'].filter(channel => pet.review_channels?.[channel]?.status === 'sent').map(channel => (
                        <div key={channel} data-testid={`pet-review-${channel}-sent`}>{channel === 'sms' ? 'SMS' : 'Email'} sent</div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Column 3: Payment Tracking */}
          <div className="space-y-6">
            {/* Payment Tracking */}
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Tracking</h3>
              <dl className="space-y-3">
                {/* Vet Staff Benefit notice */}
                {pet.vet_staff_benefit && (
                  <div className={`px-3 py-2 rounded-lg border ${pet.vet_staff_benefit === 'discount_10' ? 'bg-green-50 border-green-200' : 'bg-purple-50 border-purple-200'}`}>
                    {pet.vet_staff_benefit === 'discount_10' ? (
                      <p className="text-sm font-semibold text-green-800">🏷️ 10% Vet Staff Discount applied to total</p>
                    ) : (
                      <p className="text-sm font-semibold text-purple-800">🏺 Complimentary Urn — select urn for client, shows $0 on invoice</p>
                    )}
                  </div>
                )}
                {pet.total_cost && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Total Cost (Manual)</dt>
                    <dd className="text-sm text-gray-900">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                        💰 Manual Price: ${pet.total_cost}
                      </span>
                    </dd>
                  </div>
                )}
                
                {/* Always show calculated total for reference */}
                {pet.pet_type && pet.weight_kg && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Calculated Total</dt>
                    <dd className="text-sm text-gray-900">${calculateTotalCost(pet.pet_type, pet.weight_kg, 
                      pet.urns_selected || (pet.urn_selection ? [pet.urn_selection] : []),
                      pet.keepsakes_selected || [],
                      pet.jewelry_selected || (pet.jewelry_selection ? [pet.jewelry_selection] : []),
                      pet.transportation_fees,
                      pet.certificate_type,
                      pet.viewing_room
                    )}</dd>
                  </div>
                )}
                
                {/* Detailed Price Breakdown in View Mode */}
                {pet.pet_type && pet.weight_kg && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <dt className="text-xs font-medium text-blue-800 mb-2">💰 Price Breakdown:</dt>
                    <dd className="space-y-1 text-xs text-blue-700">
                      <div className="flex justify-between items-center">
                        <span>Base Service ({pet.pet_type}, {pet.weight_kg}kg):</span>
                        <span className="font-medium">${calculateBaseServicePrice(pet.pet_type, pet.weight_kg)}</span>
                      </div>
                      
                      {/* Show selected urns pricing - FIXED: Use NEW field format */}
                      {(pet.urns_selected && pet.urns_selected.length > 0) || pet.urn_selection ? (
                        <>
                          {pet.urns_selected && pet.urns_selected.length > 0 ? 
                            pet.urns_selected.map((urn, index) => (
                              <div key={index} className="flex justify-between items-center text-purple-700">
                                <span>• {urn}:</span>
                                <span className="font-medium">${getProductPrice(urn, 'urn')}</span>
                              </div>
                            )) : pet.urn_selection && (
                              <div className="flex justify-between items-center text-purple-700">
                                <span>• {pet.urn_selection}:</span>
                                <span className="font-medium">${getProductPrice(pet.urn_selection, 'urn')}</span>
                              </div>
                            )
                          }
                        </>
                      ) : null}
                      
                      {/* Show selected keepsakes pricing */}
                      {pet.keepsakes_selected && pet.keepsakes_selected.length > 0 && pet.keepsakes_selected.map((keepsake, index) => (
                        <div key={index} className="flex justify-between items-center text-blue-700">
                          <span>• {keepsake}:</span>
                          <span className="font-medium">${getProductPrice(keepsake, 'keepsake', pet.pet_type)}</span>
                        </div>
                      ))}
                      
                      {/* Show selected jewelry pricing - FIXED: Use NEW field format */}
                      {(pet.jewelry_selected && pet.jewelry_selected.length > 0) || pet.jewelry_selection ? (
                        <>
                          {pet.jewelry_selected && pet.jewelry_selected.length > 0 ? 
                            pet.jewelry_selected.map((jewelry, index) => (
                              <div key={index} className="flex justify-between items-center text-amber-700">
                                <span>• {jewelry}:</span>
                                <span className="font-medium">${getProductPrice(jewelry, 'jewelry')}</span>
                              </div>
                            )) : pet.jewelry_selection && (
                              <div className="flex justify-between items-center text-amber-700">
                                <span>• {pet.jewelry_selection}:</span>
                                <span className="font-medium">${getProductPrice(pet.jewelry_selection, 'jewelry')}</span>
                              </div>
                            )
                          }
                        </>
                      ) : null}
                      
                      {/* Show transportation fees */}
                      {pet.transportation_fees && (
                        Array.isArray(pet.transportation_fees) ? pet.transportation_fees : [pet.transportation_fees]
                      ).map((fee, index) => (
                        <div key={index} className="flex justify-between items-center text-green-700">
                          <span>• {fee.split('$')[0].trim()}:</span>
                          <span className="font-medium">${getProductPrice(fee, 'transportation')}</span>
                        </div>
                      ))}

                      {/* Show viewing room fee */}
                      {pet.viewing_room && (
                        <div className="flex justify-between items-center text-pink-700">
                          <span>• Viewing Room ({pet.viewing_room}):</span>
                          <span className="font-medium">${getViewingRoomPrice(pet.viewing_room)}</span>
                        </div>
                      )}
                      
                      
                      {/* Show certificate fee */}
                      {pet.certificate_type === 'Printed' && (
                        <div className="flex justify-between items-center text-red-700">
                          <span>• Printed Certificate:</span>
                          <span className="font-medium">${getProductPrice('Printed', 'certificate')}</span>
                        </div>
                      )}
                      
                      <div className="border-t border-blue-300 pt-1 mt-2">
                        <div className="flex justify-between items-center font-bold text-blue-900">
                          <span>Calculated Total:</span>
                          <span>${calculateTotalCost(pet.pet_type, pet.weight_kg, 
                            // Handle both old and new field formats for backward compatibility
                            pet.urns_selected || (pet.urn_selection ? [pet.urn_selection] : []),
                            pet.keepsakes_selected || [],
                            pet.jewelry_selected || (pet.jewelry_selection ? [pet.jewelry_selection] : []),
                            pet.transportation_fees,
                            pet.certificate_type,
                            pet.viewing_room
                          )}</span>
                        </div>
                      </div>
                    </dd>
                  </div>
                )}
                {pet.payment_method && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Payment Method</dt>
                    <dd className="text-sm text-gray-900">{pet.payment_method}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm font-medium text-gray-500">Payment Status</dt>
                  <dd className="text-sm text-gray-900">
                    {pet.is_paid ? (
                      <span className="text-green-600 font-medium">Paid</span>
                    ) : (
                      <span className="text-orange-600 font-medium">Pending</span>
                    )}
                  </dd>
                </div>
                {/* Clinic invoice / amount collected at clinic (Clinic Account etc.) */}
                {pet.client_paid && pet.clinic_payment_source && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200" data-testid="petview-clinic-invoice">
                    <dt className="text-xs font-medium text-amber-800 mb-1">💰 Clinic Invoice</dt>
                    <dd className="text-sm text-amber-900 font-semibold">
                      {pet.clinic_payment_source} — ${pet.amount_paid || 0}
                    </dd>
                  </div>
                )}
                {/* Partial payment breakdown */}
                {getPaymentSummary(pet).hasPayments && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200" data-testid="petview-payment-history">
                    <dt className="text-xs font-medium text-amber-800 mb-2">💳 Payment History</dt>
                    <dd className="space-y-1 text-xs text-amber-900">
                      {getPaymentSummary(pet).payments.map((p, idx) => (
                        <div key={p.id || idx} className="flex justify-between">
                          <span>{formatDate(p.date)} · {p.method || '—'}</span>
                          <span className="font-medium">${parseFloat(p.amount || 0)}</span>
                        </div>
                      ))}
                      <div className="border-t border-amber-300 pt-1 mt-1 flex justify-between font-semibold">
                        <span>Paid ${getPaymentSummary(pet).totalPaid} of ${getPaymentSummary(pet).totalCost}</span>
                        <span className={getPaymentSummary(pet).owing > 0 ? 'text-amber-700' : 'text-green-700'}>
                          ${getPaymentSummary(pet).owing} owing
                        </span>
                      </div>
                    </dd>
                  </div>
                )}
                {pet.referral_source && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Referral Source</dt>
                    <dd className="text-sm text-gray-900">{pet.referral_source}</dd>
                  </div>
                )}
                {pet.payment_date && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Payment Date</dt>
                    <dd className="text-sm text-gray-900">{formatDate(pet.payment_date)}</dd>
                  </div>
                )}
                {pet.weekly_pet_number && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Weekly Pet Number</dt>
                    <dd className="text-sm text-gray-900">{pet.weekly_pet_number}</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* QuickBooks Invoice */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">QuickBooks Invoice</h3>
              <QBInvoicePanel petId={pet.id} petEmail={pet.email} onCreated={fetchPet}
                existingInvoiceId={pet.qb_invoice_id}
                existingTotal={pet.qb_invoice_total}
                existingEmailSent={pet.qb_invoice_sent}
                vetStaffBenefit={pet.vet_staff_benefit}
                hasUrnSelected={!!(pet.urns_selected && pet.urns_selected.length > 0)} />
            </div>
          </div>
        </div>
      </main>
      
      {/* SMS Preview Modal */}
      <SMSPreviewModal
        isOpen={showSMSModal}
        onClose={() => {
          setShowSMSModal(false);
          setSMSModalData({
            petId: null,
            stepKey: null,
            templateKey: null,
            primaryTemplate: null,
            alternativeTemplates: [],
            renderedBody: '',
            clientName: '',
            petName: '',
            toPhone: '',
            loading: false,
            sending: false,
            error: null,
            success: false,
            isCertificateEmail: false
          });
        }}
        modalData={smsModalData}
        onTemplateChange={changeTemplate}
        onMessageEdit={(newBody) => setSMSModalData(prev => ({ ...prev, renderedBody: newBody }))}
        onSend={sendSMS}
      />


      {/* Email Preview Modal */}
      <EmailPreviewModal
        isOpen={showEmailModal}
        onClose={() => {
          setShowEmailModal(false);
          setEmailModalData({
            petId: null,
            stepKey: null,
            templateKey: null,
            subject: '',
            renderedBody: '',
            clientName: '',
            petName: '',
            toEmail: '',
            loading: false,
            sending: false,
            error: null,
            success: false,
            isCertificateEmail: false,
            availableTemplates: [],
            previousPetInfo: null
          });
        }}
        modalData={emailModalData}
        onSignatureUpdate={setEmailModalData}
        onSubjectEdit={(newSubject) => setEmailModalData(prev => ({ ...prev, subject: newSubject }))}
        onMessageEdit={(newBody) => setEmailModalData(prev => ({ ...prev, renderedBody: typeof newBody === 'function' ? newBody(prev.renderedBody) : newBody }))}
        onSend={emailModalData.isCertificateEmail ? sendCertificates : sendEmail}
        onTemplateChange={changeEmailTemplate}
        onAfterpayLinkChange={link => setEmailModalData(prev => updateAfterpayDraft(prev, link))}
      />

      {/* AI Chat Assistant with pet context */}
      <AIChatPanel contextType="pet_detail" contextPetId={petId} onRefresh={fetchPet} />

    </div>
  );
};


export default PetView;
